import { getBaseUrl, getWebBaseUrl } from '../../config/appSettings';

const pick = (obj, ...keys) => {
  for (const key of keys) {
    if (obj?.[key] != null && obj[key] !== '') return obj[key];
  }
  return undefined;
};

const unwrapList = (payload) => {
  const data = payload?.result ?? payload?.data ?? payload;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.categories)) return data.categories;
  if (Array.isArray(data?.documents)) return data.documents;
  if (Array.isArray(data?.result)) return data.result;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

export const KYC_MAX_FILE_MB = Number(process.env.REACT_APP_KYC_MAX_FILE_MB || 5);
export const KYC_MAX_FILE_BYTES = KYC_MAX_FILE_MB * 1024 * 1024;
export const KYC_ACCEPT = '.pdf,.jpg,.jpeg,.png';
export const KYC_ALLOWED_EXT = ['.pdf', '.jpg', '.jpeg', '.png'];
export const KYC_ALLOWED_MIME = ['application/pdf', 'image/jpeg', 'image/png'];

export const normalizeStatus = (value) => {
  if (value === true) return 'verified';
  // Boolean false is a flag (e.g. IsAccountVerified), not a KYC status string.
  if (value === false) return '';
  const s = String(value ?? '').trim().toLowerCase();
  if (!s) return '';
  if (['verified', 'approved', 'complete', 'completed', 'true', '1'].includes(s)) return 'verified';
  if (s.includes('reject') || ['failed', 'decline', 'declined'].includes(s)) return 'rejected';
  if (s.includes('review') || s.includes('submitted') || s === 'pending') return 'pending';
  return 'pending';
};

export const isReviewStatus = (value, reviewFlag) => {
  if (reviewFlag === true || reviewFlag === 1 || String(reviewFlag).toLowerCase() === 'true') {
    return true;
  }
  const s = String(value || '').toLowerCase();
  // Bare "pending" means not started / incomplete — not under review.
  if (!s || s === 'pending') return false;
  return /review|submitted|under.?review/.test(s) && normalizeStatus(value) !== 'verified';
};

export const categoryKind = (label = '') => {
  const s = String(label).toLowerCase();
  if (s.includes('address')) return 'address';
  if (s.includes('identity') || s.includes('account')) return 'identity';
  return 'other';
};

export const categoryDescription = (label = '', apiDesc) => {
  if (apiDesc) return String(apiDesc);
  const kind = categoryKind(label);
  if (kind === 'identity') {
    return 'Government-issued ID with photo (passport, national ID, driving licence, or residence permit) and a matching selfie.';
  }
  if (kind === 'address') {
    return 'Proof of address issued within the last 3 months (utility bill, bank or credit card statement).';
  }
  return 'Upload the required documents for this category.';
};

export const orderKycSteps = (steps = []) => {
  const list = [...steps];
  const addressIdx = list.findIndex((s) => categoryKind(s.label) === 'address');
  const identityIdx = list.findIndex((s) => categoryKind(s.label) === 'identity');
  if (addressIdx > 0 && identityIdx >= 0 && addressIdx > identityIdx) {
    const [addr] = list.splice(addressIdx, 1);
    list.splice(identityIdx, 0, addr);
  } else if (addressIdx > 0 && identityIdx < 0) {
    const [addr] = list.splice(addressIdx, 1);
    list.unshift(addr);
  }
  return list;
};

export const normalizeCategories = (payload) => {
  const list = unwrapList(payload);
  if (!list.length) return [];
  return withoutAddressSteps(
    orderKycSteps(
      list.map((row) => {
        const label = String(
          pick(row, 'documentCategoryName', 'DocumentCategoryName', 'name', 'label') || 'Category'
        );
        const desc = pick(row, 'description', 'Description', 'desc');
        return {
          id: Number(pick(row, 'documentCategoryId', 'DocumentCategoryId', 'id')),
          label,
          desc: categoryDescription(label, desc),
          kind: categoryKind(label),
          completionPercentage: Number(pick(row, 'completionPercentage', 'CompletionPercentage') || 0),
        };
      }).filter((c) => Number.isFinite(c.id) && c.id > 0)
    )
  );
};

/** Address verification is disabled — hide those steps from the partner UI. */
export const withoutAddressSteps = (steps) => {
  if (!Array.isArray(steps)) return [];
  return steps.filter((s) => {
    const label = String(s.label || '').toLowerCase();
    return s.kind !== 'address' && !label.includes('address');
  });
};

export const normalizeDocumentTypes = (payload) => {
  const list = unwrapList(payload);
  return list
    .map((row) => ({
      id: Number(pick(row, 'documentTypeId', 'DocumentTypeId', 'id')),
      label: String(
        pick(row, 'documentName', 'DocumentName', 'documentTypeName', 'DocumentTypeName', 'name', 'label') ||
          'Document'
      ),
      categoryId: Number(pick(row, 'documentCategoryId', 'DocumentCategoryId') || 0),
    }))
    .filter((t) => Number.isFinite(t.id) && t.id > 0);
};

export const resolveKycFileUrl = (path, fileUrl) => {
  const publicUrl = String(fileUrl || '').trim();
  if (/^https?:\/\//i.test(publicUrl) || publicUrl.startsWith('blob:') || publicUrl.startsWith('data:')) {
    return publicUrl;
  }
  const raw = String(path || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw) || raw.startsWith('blob:') || raw.startsWith('data:')) return raw;
  const base = (getWebBaseUrl() || getBaseUrl() || '').replace(/\/$/, '');
  return `${base}/${raw.replace(/^\//, '')}`;
};

export const normalizeDocuments = (payload) => {
  const list = unwrapList(payload);
  return list.map((row) => {
    const fileName = String(pick(row, 'fileName', 'FileName', 'path', 'FilePath', 'url') || '');
    const filePath = String(pick(row, 'filePath', 'FilePath', 'path', 'url', 'fileName', 'FileName') || '');
    return {
      userDocumentId: pick(row, 'affiliateDocumentId', 'AffiliateDocumentId', 'userDocumentId', 'id'),
      documentTypeId: Number(pick(row, 'documentTypeId', 'DocumentTypeId') || 0),
      documentCategoryId: Number(pick(row, 'documentCategoryId', 'DocumentCategoryId') || 0),
      documentTypeName: String(
        pick(row, 'documentTypeName', 'DocumentTypeName', 'documentName', 'DocumentName') || 'Document'
      ),
      documentCategoryName: String(
        pick(row, 'documentCategoryName', 'DocumentCategoryName') || ''
      ),
      fileName,
      filePath,
      url: resolveKycFileUrl(
        filePath || fileName,
        pick(row, 'fileUrl', 'FileUrl', 'url', 'Url')
      ),
      status: normalizeStatus(pick(row, 'documentStatus', 'DocumentStatus', 'status', 'Status')),
      statusRaw: String(pick(row, 'documentStatus', 'DocumentStatus', 'status', 'Status') || 'Pending'),
      side: String(
        pick(row, 'description', 'Description', 'documentSide', 'DocumentSide', 'side', 'Side', 'fileSide') || ''
      ),
      remarks: pick(row, 'remarks', 'Remarks') || '',
      createdDateTime: pick(row, 'createdDateTime', 'CreatedDateTime', 'createdAt', 'CreatedAt') || '',
    };
  });
};

const documentCreatedAt = (doc = {}) => {
  const raw = doc.createdDateTime || doc.CreatedDateTime || '';
  if (!raw) return 0;
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : 0;
};

/** Keep only the newest upload batch (Front/Back/Selfie share one CreatedDateTime). */
export const latestDocumentBatch = (docs = []) => {
  if (!Array.isArray(docs) || docs.length === 0) return [];
  const maxTime = Math.max(...docs.map(documentCreatedAt), 0);
  if (maxTime > 0) {
    const sameTime = docs.filter((d) => documentCreatedAt(d) === maxTime);
    if (sameTime.length) return sameTime;
  }
  const maxId = Math.max(...docs.map((d) => Number(d.userDocumentId) || 0), 0);
  const newest = docs.find((d) => Number(d.userDocumentId) === maxId);
  if (!newest) return docs;
  const typeId = Number(newest.documentTypeId);
  if (!typeId) return [newest];
  return docs.filter((d) => Number(d.documentTypeId) === typeId);
};

/** Guess category kind from document type / file name when API omits category. */
export const inferKindFromDocument = (doc = {}) => {
  const blob = `${doc.documentCategoryName || ''} ${doc.documentTypeName || ''} ${doc.fileName || ''}`.toLowerCase();
  if (
    blob.includes('address') ||
    blob.includes('utility') ||
    blob.includes('bank') ||
    blob.includes('credit') ||
    blob.includes('statement') ||
    blob.includes('bill')
  ) {
    return 'address';
  }
  if (
    blob.includes('identity') ||
    blob.includes('account') ||
    blob.includes('passport') ||
    blob.includes('national') ||
    blob.includes('driving') ||
    blob.includes('licence') ||
    blob.includes('license') ||
    blob.includes('residence') ||
    /\bid\b/.test(blob) ||
    blob.includes('front_') ||
    blob.includes('back_')
  ) {
    return 'identity';
  }
  return 'other';
};

/**
 * Collect documents from status payload:
 * top-level documents[], category.documents[], and myDocuments fallback.
 */
export const collectStatusDocuments = (payload, documentsFallback = []) => {
  const root = payload?.result ?? payload?.data ?? payload ?? {};
  const data = root && typeof root === 'object' ? root : {};
  const buckets = [];

  const push = (src) => {
    if (src == null) return;
    const list = normalizeDocuments(src);
    if (list.length) buckets.push(...list);
  };

  push(data?.documents ?? data?.Documents);
  push(data?.myDocuments ?? data?.MyDocuments);
  push(documentsFallback);

  const categoryRows = unwrapList(
    data?.categories ?? data?.Categories ?? data?.documentCategories ?? data?.DocumentCategories
  );
  categoryRows.forEach((row) => {
    const catName = String(
      pick(row, 'documentCategoryName', 'DocumentCategoryName', 'categoryName', 'name', 'label') || ''
    );
    const catId = Number(pick(row, 'documentCategoryId', 'DocumentCategoryId', 'id') || 0);
    const nested = row?.documents ?? row?.Documents ?? row?.files ?? row?.Files;
    normalizeDocuments(nested).forEach((doc) => {
      buckets.push({
        ...doc,
        documentCategoryId: doc.documentCategoryId || catId || 0,
        documentCategoryName: doc.documentCategoryName || catName,
      });
    });
  });

  const seen = new Set();
  return buckets.filter((doc) => {
    const key =
      doc.userDocumentId != null
        ? `id:${doc.userDocumentId}`
        : `f:${doc.filePath || doc.fileName}|${doc.documentTypeId}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** Map documentTypeId → category using lazy-loaded types + category labels. */
export const enrichDocumentsWithCategories = (documents = [], categories = [], typesByCategory = {}) => {
  const typeToCategoryId = {};
  Object.entries(typesByCategory || {}).forEach(([catId, types]) => {
    (types || []).forEach((t) => {
      if (t?.id) typeToCategoryId[Number(t.id)] = Number(catId);
    });
  });

  const catById = Object.fromEntries((categories || []).map((c) => [Number(c.id), c]));
  const catByKind = {
    identity: (categories || []).find((c) => c.kind === 'identity'),
    address: (categories || []).find((c) => c.kind === 'address'),
  };

  return (documents || []).map((doc) => {
    let categoryId = Number(doc.documentCategoryId) || 0;
    let categoryName = String(doc.documentCategoryName || '');

    if (!categoryId && doc.documentTypeId && typeToCategoryId[Number(doc.documentTypeId)]) {
      categoryId = typeToCategoryId[Number(doc.documentTypeId)];
    }
    if (!categoryName && categoryId && catById[categoryId]) {
      categoryName = catById[categoryId].label || '';
    }
    if (!categoryId && categoryName) {
      const kind = categoryKind(categoryName);
      const match = (categories || []).find((c) => c.kind === kind);
      if (match) categoryId = match.id;
    }
    if (!categoryId) {
      const inferred = inferKindFromDocument({ ...doc, documentCategoryName: categoryName });
      const match = catByKind[inferred];
      if (match) {
        categoryId = match.id;
        categoryName = categoryName || match.label;
      }
    }
    if (!categoryName && categoryId && catById[categoryId]) {
      categoryName = catById[categoryId].label || '';
    }

    return {
      ...doc,
      documentCategoryId: categoryId,
      documentCategoryName: categoryName,
    };
  });
};

export const docsForCategory = (documents = [], category = {}, typesByCategory = {}) => {
  const categoryId = Number(category.id);
  const kind = category.kind || categoryKind(category.label);
  const typeIds = new Set(
    ((typesByCategory && typesByCategory[categoryId]) || []).map((t) => Number(t.id)).filter(Boolean)
  );

  const matched = (documents || []).filter((d) => {
    if (categoryId && Number(d.documentCategoryId) === categoryId) return true;
    if (typeIds.size && d.documentTypeId && typeIds.has(Number(d.documentTypeId))) return true;
    const name = `${d.documentCategoryName || ''}`.toLowerCase();
    if (kind === 'address' && name.includes('address')) return true;
    if (kind === 'identity' && (name.includes('identity') || name.includes('account'))) return true;
    if (inferKindFromDocument(d) === kind) return true;
    return false;
  });
  return latestDocumentBatch(matched);
};

/**
 * Identity uploads are Front then Back (same document type).
 * Prefer API side field; otherwise label by upload order within the category.
 */
export const withDocumentDisplayLabels = (docs = [], stepKind = 'other') => {
  const sorted = [...docs].sort((a, b) => {
    const idA = Number(a.userDocumentId) || 0;
    const idB = Number(b.userDocumentId) || 0;
    if (idA && idB && idA !== idB) return idA - idB;
    return String(a.fileName || '').localeCompare(String(b.fileName || ''));
  });

  return sorted.map((doc, index) => {
    const raw = String(doc.side || doc.description || doc.fileName || '').toLowerCase();
    let sideLabel = '';
    if (/\bfront\b/.test(raw)) sideLabel = 'Front';
    else if (/\bback\b/.test(raw)) sideLabel = 'Back';
    else if (/\bselfie\b/.test(raw)) sideLabel = 'Selfie';
    else if (stepKind === 'identity') {
      if (sorted.length === 1) sideLabel = 'Front';
      else if (index === 0) sideLabel = 'Front';
      else if (index === 1) sideLabel = 'Back';
      else sideLabel = `File ${index + 1}`;
    }

    const typeName = doc.documentTypeName || 'Document';
    return {
      ...doc,
      displayLabel: sideLabel ? `${typeName} — ${sideLabel}` : typeName,
      sideLabel,
    };
  });
};

const sideStatusFromDocs = (docs = []) => {
  const latest = latestDocumentBatch(docs);
  if (!latest.length) return { status: 'pending', inReview: false };
  if (latest.some((d) => d.status === 'verified')) return { status: 'verified', inReview: false };
  if (latest.some((d) => d.status === 'rejected') && !latest.some((d) => d.status === 'pending')) {
    return { status: 'rejected', inReview: false };
  }
  if (latest.some((d) => d.status === 'pending')) return { status: 'pending', inReview: true };
  if (latest.some((d) => d.status === 'rejected')) return { status: 'rejected', inReview: false };
  return { status: 'pending', inReview: true };
};

export const formatDocStatusLabel = (status, statusRaw) => {
  if (status === 'rejected') return 'Rejected';
  if (status === 'verified') return 'Verified';
  const raw = String(statusRaw || '').trim();
  if (/reject/i.test(raw)) return 'Rejected';
  if (/review/i.test(raw)) return 'In Review';
  return raw || 'Pending';
};

export const parseKycStatusPayload = (payload, documentsFallback = []) => {
  const root = payload?.result ?? payload?.data ?? payload ?? {};
  const data = root && typeof root === 'object' ? root : {};

  const resolvedDocs = collectStatusDocuments(data, documentsFallback);

  const identityDocs = resolvedDocs.filter((d) => {
    const blob = `${d.documentCategoryName}`.toLowerCase();
    return (
      blob.includes('identity') ||
      blob.includes('account') ||
      inferKindFromDocument(d) === 'identity'
    );
  });
  const addressDocs = resolvedDocs.filter(
    (d) =>
      `${d.documentCategoryName}`.toLowerCase().includes('address') ||
      inferKindFromDocument(d) === 'address'
  );

  // Docs without category name still count as progress (matched later via type→category map)
  const unclassifiedDocs = resolvedDocs.filter(
    (d) => !identityDocs.includes(d) && !addressDocs.includes(d)
  );

  const identityFromDocs = sideStatusFromDocs(identityDocs);
  const addressFromDocs = sideStatusFromDocs(addressDocs);
  const unclassifiedFromDocs = sideStatusFromDocs(unclassifiedDocs);

  // GetAffiliateKYCStatus may return categories[{ name/status }] instead of flat fields
  const categoryRows = unwrapList(
    data?.categories ?? data?.Categories ?? data?.documentCategories ?? data?.DocumentCategories
  );
  const categoryStatusByKind = { identity: '', address: '' };
  categoryRows.forEach((row) => {
    const label = String(
      pick(row, 'documentCategoryName', 'DocumentCategoryName', 'categoryName', 'name', 'label') || ''
    );
    const statusVal = pick(
      row,
      'verificationStatus',
      'VerificationStatus',
      'status',
      'Status',
      'documentStatus',
      'DocumentStatus',
      'categoryStatus'
    );
    const kind = categoryKind(label);
    if (kind === 'identity' || kind === 'address') {
      categoryStatusByKind[kind] = normalizeStatus(statusVal);
    }
  });

  const identityVerification =
    normalizeStatus(pick(data, 'identityVerification', 'IdentityVerification')) ||
    categoryStatusByKind.identity ||
    identityFromDocs.status ||
    'pending';

  const accountVerification =
    normalizeStatus(pick(data, 'identityVerification', 'IdentityVerification')) ||
    categoryStatusByKind.identity ||
    identityVerification;

  const addressVerification =
    normalizeStatus(pick(data, 'addressVerification', 'AddressVerification')) ||
    categoryStatusByKind.address ||
    addressFromDocs.status;

  const accountVerificationReview =
    pick(data, 'accountVerificationReview', 'AccountVerificationReview') != null
      ? Boolean(pick(data, 'accountVerificationReview', 'AccountVerificationReview'))
      : identityVerification !== 'verified' &&
        (identityFromDocs.inReview ||
          isReviewStatus(
            pick(data, 'identityVerification', 'IdentityVerification', 'accountVerification', 'AccountVerification'),
            false
          ));

  const addressVerificationReview =
    pick(data, 'addressVerificationReview', 'AddressVerificationReview') != null
      ? Boolean(pick(data, 'addressVerificationReview', 'AddressVerificationReview'))
      : addressVerification !== 'verified' &&
        (addressFromDocs.inReview ||
          isReviewStatus(pick(data, 'addressVerification', 'AddressVerification'), false));

  const verificationStatusRaw = pick(
    data,
    'verificationStatus',
    'VerificationStatus',
    'kycStatus',
    'KycStatus',
    'docsStatus',
    'DocsStatus'
  );
  const isVerifiedFlag = pick(
    data,
    'isVerified',
    'IsVerified',
    'verified',
    'Verified',
    'isKycVerified',
    'IsKycVerified',
    'isAccountVerified',
    'IsAccountVerified',
    'isIdentityVerified',
    'IsIdentityVerified'
  );
  let isVerified =
    isVerifiedFlag === true ||
    String(isVerifiedFlag).toLowerCase() === 'true' ||
    normalizeStatus(verificationStatusRaw) === 'verified';

  let docsStatus = normalizeStatus(verificationStatusRaw || pick(data, 'docsStatus', 'DocsStatus'));
  const docsStatusRaw = String(verificationStatusRaw || pick(data, 'docsStatus', 'DocsStatus') || '');

  const identityApproved =
    identityVerification === 'verified' || accountVerification === 'verified';

  if (!verificationStatusRaw && !pick(data, 'docsStatus', 'DocsStatus')) {
    if (identityApproved) {
      docsStatus = 'verified';
    } else if (identityVerification === 'rejected') {
      docsStatus = 'rejected';
    } else {
      docsStatus = 'pending';
    }
  }

  if (!isVerified && docsStatus === 'verified') isVerified = true;
  // Address verification is disabled — account/identity alone is enough.
  if (!isVerified && identityApproved) {
    isVerified = true;
    docsStatus = 'verified';
  }

  // Fully approved → clear review flags
  let accountReview = accountVerificationReview;
  let addressReview = addressVerificationReview;
  if (isVerified || accountVerification === 'verified') accountReview = false;
  if (isVerified || addressVerification === 'verified') addressReview = false;
  if (identityFromDocs.status === 'rejected' || identityVerification === 'rejected') {
    docsStatus = 'rejected';
    accountReview = false;
  }

  const inReviewOverall =
    !isVerified &&
    docsStatus !== 'rejected' &&
    identityVerification !== 'rejected' &&
    (/review/i.test(docsStatusRaw) ||
      (docsStatus === 'pending' &&
        (accountReview || addressReview || unclassifiedFromDocs.inReview)));

  const hasDocuments = resolvedDocs.length > 0;
  const notStarted =
    !isVerified &&
    !hasDocuments &&
    !accountReview &&
    !addressReview &&
    identityVerification === 'pending' &&
    addressVerification === 'pending' &&
    docsStatus === 'pending';

  return {
    isVerified,
    docsStatus,
    verificationStatus: docsStatusRaw || docsStatus,
    inReviewOverall,
    notStarted,
    accountVerification,
    identityVerification,
    addressVerification,
    accountVerificationReview: accountReview,
    addressVerificationReview: addressReview,
    documents: resolvedDocs,
    identityDocs,
    addressDocs,
    categories: categoryRows,
  };
};

export const stepUiState = (step, status, docsForStep = []) => {
  const kind = step.kind || categoryKind(step.label);
  const sideStatus =
    kind === 'address'
      ? status.addressVerification
      : status.accountVerification || status.identityVerification || status.docsStatus;
  const inReview =
    kind === 'address' ? status.addressVerificationReview : status.accountVerificationReview;
  const hasDocs = Array.isArray(docsForStep) && docsForStep.length > 0;
  const fromDocs = sideStatusFromDocs(docsForStep);

  if (sideStatus === 'verified' || status.isVerified) {
    return { key: 'verified', badge: 'Verified', canExpand: false, canUpload: false };
  }
  if (fromDocs.status === 'rejected' || sideStatus === 'rejected' || status.docsStatus === 'rejected') {
    return { key: 'rejected', badge: 'Rejected', canExpand: true, canUpload: true };
  }
  // In review with files → lock upload. Without files → still allow upload (avoid stuck UI).
  if ((inReview || isReviewStatus(sideStatus, inReview) || fromDocs.inReview) && hasDocs) {
    return { key: 'review', badge: 'In Review', canExpand: true, canUpload: false };
  }
  return { key: 'pending', badge: 'Upload', canExpand: true, canUpload: true };
};

export const overallBanner = (status = {}) => {
  if (status.isVerified || status.docsStatus === 'verified') {
    return {
      key: 'verified',
      badge: 'Verified',
      message: 'Your KYC is complete. All features are enabled.',
    };
  }
  if (status.docsStatus === 'rejected' || status.identityVerification === 'rejected' || status.accountVerification === 'rejected') {
    return {
      key: 'rejected',
      badge: 'Rejected',
      message: 'Some documents were rejected. Please re-upload or contact support.',
    };
  }
  if (status.inReviewOverall || status.accountVerificationReview || status.addressVerificationReview) {
    return {
      key: 'review',
      badge: 'In Review',
      message: 'Your documents are under review. We will notify you once verification is complete.',
    };
  }
  if (status.notStarted) {
    return {
      key: 'pending',
      badge: 'Not Started',
      message: 'Complete verification to unlock all features.',
    };
  }
  return {
    key: 'pending',
    badge: 'Pending',
    message: 'Your KYC is partially complete. Complete verification to enable withdrawals.',
  };
};

export const validateKycFile = (file) => {
  if (!file) return 'Please select a file';
  const name = String(file.name || '').toLowerCase();
  const ext = name.includes('.') ? `.${name.split('.').pop()}` : '';
  if (!KYC_ALLOWED_EXT.includes(ext)) {
    return `File type not allowed. Use PDF, JPG, or PNG (max ${KYC_MAX_FILE_MB} MB).`;
  }
  if (file.type && !KYC_ALLOWED_MIME.includes(file.type) && file.type !== 'image/jpg') {
    // some browsers omit type — extension already checked
    if (file.type && !file.type.startsWith('image/') && file.type !== 'application/pdf') {
      return `File type not allowed. Use PDF, JPG, or PNG (max ${KYC_MAX_FILE_MB} MB).`;
    }
  }
  if (file.size > KYC_MAX_FILE_BYTES) {
    return `File is too large. Max size is ${KYC_MAX_FILE_MB} MB.`;
  }
  const blocked = /\.(exe|js|html?|php|sh|bat|cmd|msi|dll)$/i;
  if (blocked.test(name)) return 'This file type is blocked for security reasons.';
  return '';
};

export const validateKycSelfieFile = (file) => {
  const err = validateKycFile(file);
  if (err) return err;
  const mime = String(file.type || '').toLowerCase();
  const name = String(file.name || '').toLowerCase();
  const isImage = mime.startsWith('image/') || /\.(jpe?g|png)$/i.test(name);
  if (!isImage || mime === 'application/pdf' || name.endsWith('.pdf')) {
    return 'Selfie must be a JPG or PNG image.';
  }
  return '';
};

export const exampleImageForType = (label = '') => {
  const s = String(label).toLowerCase();
  if (s.includes('passport')) return '/documentimages/passport-dos-donts.png';
  if (s.includes('driving') || s.includes('license') || s.includes('licence') || /\bdl\b/.test(s)) {
    return '/documentimages/dl-dos-donts.png';
  }
  if (s.includes('utility') || s.includes('bill')) return '/documentimages/utility-dos-donts.png';
  if (s.includes('bank') || s.includes('statement')) return '/documentimages/bank-statement.png';
  if (s.includes('credit')) return '/documentimages/credit-card-statement.png';
  if (s.includes('national') || s.includes('identity') || s.includes('id card') || s.includes('residence')) {
    return '/documentimages/identity-card.png';
  }
  return '';
};

export const acceptedDocsHelp = (step) => {
  const kind = step?.kind || categoryKind(step?.label);
  if (kind === 'identity') {
    return 'Passport, national ID, driving licence, or residence permit with a clear photo and a matching selfie (upload or live capture).';
  }
  if (kind === 'address') {
    return 'Address verification is currently disabled.';
  }
  return step?.desc || 'Upload clear scans or photos of the required documents.';
};

export const isKycVerifiedFromApiResult = (result) => {
  if (!result) return false;
  if (
    result.isVerified === true ||
    result.verified === true ||
    result.isKycVerified === true ||
    result.isAccountVerified === true ||
    result.isIdentityVerified === true
  ) {
    return true;
  }
  if (
    normalizeStatus(result.accountVerification) === 'verified' ||
    normalizeStatus(result.identityVerification) === 'verified'
  ) {
    return true;
  }
  return (
    normalizeStatus(result.docsStatus) === 'verified' ||
    normalizeStatus(result.verificationStatus) === 'verified'
  );
};

/** Normalize login / profile payload KYC flags for navbar + sidebar. */
export const withUserKycFlags = (data) => {
  if (!data || typeof data !== 'object') return data;
  const verified = isKycVerifiedFromApiResult({
    isVerified: data.isVerified ?? data.IsVerified,
    verified: data.verified ?? data.Verified,
    isKycVerified: data.isKycVerified ?? data.IsKycVerified,
    isAccountVerified: data.isAccountVerified ?? data.IsAccountVerified,
    isIdentityVerified: data.isIdentityVerified ?? data.IsIdentityVerified,
    accountVerification: data.accountVerification ?? data.AccountVerification,
    identityVerification: data.identityVerification ?? data.IdentityVerification,
    docsStatus: data.docsStatus ?? data.DocsStatus,
    verificationStatus:
      data.verificationStatus ?? data.VerificationStatus ?? data.kycStatus ?? data.KycStatus,
  });
  return {
    ...data,
    isVerified: verified || Boolean(data.isVerified ?? data.IsVerified),
    verified: verified || Boolean(data.verified ?? data.Verified),
  };
};
